import * as React from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { css, SerializedStyles } from '@emotion/react';
import { FixMeLater } from '../../../utils';
import { useSelectMenuContext } from '../SelectMenu.context';
import { Styler, StylerProps } from '../../Styler';
import { ButtonBase, ButtonBaseProps } from '../../ButtonBase';
import { ChevronDownIcon } from '../../../icons/ChevronDownIcon';
import { SvgIconProps } from '../../SvgIcon';

export type SelectMenuButtonClasses = 'root' | 'icon' | 'chevron';

// Static styles
const STATIC_STYLES: { [key in SelectMenuButtonClasses]: SerializedStyles } = {
    root: css`
        --height: 32px;

        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: var(--height);
        padding: 0 calc(var(--hc-spacing-unit) * 2);
        border-radius: 8px;
        gap: 6px;
        font-size: 0.875rem;
        font-weight: var(--hc-body-font-weight-semi-bold);
        color: var(--hc-color-text-field);

        transition: background-color var(--hc-transition-duration-shortest) var(--hc-transition-easing-ease-in-out);

        &:hover,
        &:focus,
        &[data-status='open'],
        &[data-status='close'] {
            background-color: var(--hc-color-neutral-tint-100);
        }
    `,
    icon: css`
        width: 16px;
        height: 16px;
        color: var(--hc-color-primary-base);
    `,
    chevron: css`
        width: 12px;
        height: 12px;
        color: currentColor;
        transition: transform var(--hc-transition-duration-standard) var(--hc-transition-easing-ease-in-out);

        &[data-rotate] {
            transform: rotate(180deg);
        }
    `,
};

export interface SelectMenuButtonProps extends StylerProps, ButtonBaseProps {
    /**
     * An svg icon component. The icon will be displayed before the label.
     */
    icon?: React.ReactElement<SvgIconProps>;
}

export const SelectMenuButton: React.ForwardRefExoticComponent<
    SelectMenuButtonProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function PopoverAnchor(
    props: SelectMenuButtonProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { icon, ...other } = props;

    const { refs, getReferenceProps, selectedOption, transitionStatus, placement } = useSelectMenuContext();
    const handleRef = useMergeRefs([refs.setReference, ref]);

    return (
        <Styler
            component={ButtonBase}
            ref={handleRef}
            baseStyles={STATIC_STYLES.root}
            {...getReferenceProps()}
            data-status={transitionStatus}
            data-placement={placement}
            data-focus-ring={true}
            {...other}
        >
            {icon &&
                React.cloneElement(icon, {
                    css: STATIC_STYLES.icon,
                    'data-icon': 'true',
                })}

            {selectedOption?.label}
            <ChevronDownIcon
                css={STATIC_STYLES.chevron}
                data-chevron={true}
                data-rotate={transitionStatus === 'open' || undefined}
            />
        </Styler>
    );
});
